import axiosInstance from '../axiosInstance'

// Models
import { SiteID } from '../sites/models/site'
import { Shift, ShiftDto, shiftFromDto, ShiftID } from './models/shift'
import { Pagination } from '../common/models/pagination'
import { Building } from '../buildings/models/building'

// Other
import { DateTime } from 'luxon'
import { Area, AreaID } from '../areas/models/area'
import { Employee, EmployeeID } from '../employees/models/employee'
import { Skill, SkillID, SkillJson } from '../skills/models/skill'
import { ShiftReasonID } from '../shift_reasons/models/shiftReason'
import { ShiftActivity, ShiftActivityDto, shiftActivityFromDto } from './models/shiftActivity'
import { formatForServer } from '../common/utils/formatDate'
import { ShiftOfferStatus } from '../employee_shift/models/employeeShiftOffer'

export interface ShiftSearchForm {
    dateFrom?: DateTime
    dateTo?: DateTime
    byCreatedDate?: boolean

    status?: 'active' | 'cancelled' | 'closed'
    fillStatus?: 'all' | 'filled' | 'unfilled' | 'awaiting_approval'

    inProgress?: boolean
    inProgressBuffer?: number

    hasResponses?: boolean

    building?: Building | null
    area?: Area | null
    skill?: Skill | null
    creator?: Employee | null
    employee?: Employee | null
    employeeId?: EmployeeID | null
    employeeOfferStatus?: ShiftOfferStatus | null

    rateMin?: number
    rateMax?: number

    perPage: number
    page: number
}

interface ShiftSearchParams {
    shift_date_after?: string
    shift_date_before?: string

    created_from?: string
    created_to?: string

    status?: 'active' | 'cancelled' | 'closed'
    fill_status?: 'all' | 'filled' | 'unfilled' | 'awaiting_approval'

    in_progress?: 0 | 1
    in_progress_buffer?: number

    has_responses?: 0 | 1

    building_id?: string
    area_id?: string
    skill_id?: string
    creator_id?: string
    employee_id?: string
    employee_offer_status?: string[]

    rate_min?: number
    rate_max?: number

    per_page: number
    page: number
    includes?: string[]

    sort_column?: 'id' | 'start_date' | 'created_at'
    sort_direction?: 'asc' | 'desc'
}


export interface ShiftUpdateForm {
    shiftId: ShiftID
    areaId?: AreaID
    startDate?: DateTime
    endDate?: DateTime
    note?: string
    //
    rateAmount?: number
}

interface ShiftUpdateData {
    area_id?: AreaID
    start_date?: string
    end_date?: string
    note?: string
    //
    rate_amount?: number
}

function searchFormToParams(form: ShiftSearchForm): ShiftSearchParams {
    const params: ShiftSearchParams = {
        per_page: form.perPage,
        page: form.page,
        includes: ['area', 'tags'],
    }

    if (form.inProgress) {
        params.in_progress = 1
        params.in_progress_buffer = form.inProgressBuffer ?? 0
    }

    if (form.hasResponses) {
        params.has_responses = 1
    }

    if (form.building) {
        params.building_id = form.building.id
    }

    if (form.area) {
        params.area_id = form.area.id
    }

    if (form.skill) {
        params.skill_id = form.skill.id
    }

    if (form.creator) {
        params.creator_id = form.creator.id
    }

    if (form.employee) {
        params.employee_id = form.employee.id
    }

    if (form.employeeId) {
        params.employee_id = form.employeeId
    }

    if (form.employeeOfferStatus) {
        params.employee_offer_status = [form.employeeOfferStatus]
    }

    if (form.rateMin) {
        params.rate_min = form.rateMin
    }

    if (form.rateMax) {
        params.rate_max = form.rateMax
    }

    if (form.status) {
        params.status = form.status
    }

    if (form.fillStatus && form.fillStatus !== 'all') {
        params.fill_status = form.fillStatus
    }

    const dateFrom = form.dateFrom
    const dateTo = form.dateTo
    if (form.byCreatedDate) {
        if (dateFrom) {
            params.created_from = formatForServer(dateFrom)
        }
        if (dateTo) {
            params.created_to = formatForServer(dateTo)
        }

        params.sort_column = 'created_at'
        params.sort_direction = 'desc'
    } else {
        if (dateFrom) {
            params.shift_date_after = formatForServer(dateFrom)
        }
        if (dateTo) {
            params.shift_date_before = formatForServer(dateTo)
        }
    }

    return params
}

export const fetch = async (siteId: SiteID, form: ShiftSearchForm): Promise<{ shifts: Shift[], pagination: Pagination }> => {
    const params = searchFormToParams(form)

    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts`, { params })

    const data = response.data.data as ShiftDto[]

    return {
        shifts: data.map((shift) => {
            return shiftFromDto(shift)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const generateExport = async (siteId: SiteID, form: ShiftSearchForm): Promise<void> => {
    const params = searchFormToParams(form)

    await axiosInstance.post(`api/orgs/${siteId}/shifts/export`, params)
}

export const show = async (siteId: SiteID, shiftId: ShiftID): Promise<{ shift: Shift }> => {
    const params = {
        includes: [
            'area',
            'creator',
        ],
    }

    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/${shiftId}`, { params })

    const data = response.data.data as ShiftDto

    return {
        shift: shiftFromDto(data),
    }
}

function updateFormToData(form: ShiftUpdateForm): ShiftUpdateData {
    const data: ShiftUpdateData = {}

    if (form.areaId) {
        data.area_id = form.areaId
    }

    if (form.startDate) {
        data.start_date = form.startDate.toUTC().toISO()
    }

    if (form.endDate) {
        data.end_date = form.endDate.toUTC().toISO()
    }

    if (form.note) {
        data.note = form.note
    }

    if (form.rateAmount) {
        data.rate_amount = form.rateAmount
    }

    return data
}

export const update = async (siteId: SiteID, shiftId: ShiftID, form: ShiftUpdateForm): Promise<{ shift: Shift }> => {
    const data = updateFormToData(form)

    const response = await axiosInstance.patch(`api/orgs/${siteId}/shifts/${shiftId}`, data)

    const shiftJson = response.data.data as ShiftDto

    return {
        shift: shiftFromDto(shiftJson)
    }
}

export const publish = async (siteId: SiteID, shiftId: ShiftID): Promise<void> => {
    await axiosInstance.put(`api/orgs/${siteId}/shifts/${shiftId}/publish`)
}


export const unpublish = async (siteId: SiteID, shiftId: ShiftID): Promise<void> => {
    await axiosInstance.delete(`api/orgs/${siteId}/shifts/${shiftId}/publish`)
}


export interface ShiftPublishBulkForm {
    shiftIds: ShiftID[]
}

interface ShiftPublishBulkData {
    shift_ids: ShiftID[]
}

export const publishBulk = async (siteId: SiteID, form: ShiftPublishBulkForm): Promise<void> => {
    const data: ShiftPublishBulkData = {
        shift_ids: form.shiftIds
    }

    await axiosInstance.post(`api/orgs/${siteId}/shifts/publish/bulk`, data)
}

export const cancel = async (siteId: SiteID, shiftId: ShiftID): Promise<void> => {
    await axiosInstance.post(`api/orgs/${siteId}/shifts/${shiftId}/cancel`)
}

export const destroy = async (siteId: SiteID, shiftId: ShiftID): Promise<void> => {
    await axiosInstance.delete(`api/orgs/${siteId}/shifts/${shiftId}`)
}

export const skillIndex = async (siteId: SiteID, shiftId: ShiftID): Promise<{ skills: Skill[], pagination: Pagination }> => {
    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/${shiftId}/skills`)

    const data = response.data.data as SkillJson[]

    return {
        skills: data.map((skill) => {
            return Skill.fromJson(skill)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const skillSync = async (siteId: SiteID, shiftId: ShiftID, form: { skillIds: SkillID[] }): Promise<void> => {
    await axiosInstance.post(`api/orgs/${siteId}/shifts/${shiftId}/skills`, { skill_ids: form.skillIds })
}

export interface ShiftCreateForm {
    startDate?: DateTime
    endDate?: DateTime
    shiftDates?: { startDate: DateTime, endDate: DateTime }[]

    published?: boolean

    // Related
    assigneeId?: EmployeeID
    areaId: AreaID
    reasonId?: ShiftReasonID
    // Rate
    rateAmount?: number
    rateCurrency?: string
    rateType?: string
    // Flags
    approvalRequired?: boolean
    // Employees
    open?: boolean
    isQueue?: boolean
    employeeIds?: EmployeeID[]
    // Other
    note?: string
    skillIds?: SkillID[]
}

interface ShiftCreateData {
    start_date?: string
    end_date?: string
    shift_dates?: { start_date: string, end_date: string }[]

    published?: boolean

    // Related
    assignee_id?: string
    area_id: string
    reason_id?: string
    // Rate
    rate_amount?: number
    rate_currency?: string
    rate_type?: string
    // Flags
    open?: boolean
    approval_required?: boolean
    queue?: boolean
    // Other
    note?: string
    skill_ids?: SkillID[]
    employee_ids?: EmployeeID[]
    // Not implemented
    on_call?: boolean
    urgent?: boolean
}

function createFormToData(form: ShiftCreateForm): ShiftCreateData {
    const data: ShiftCreateData = {
        area_id: form.areaId,
    }

    if (form.shiftDates) {
        data.shift_dates = form.shiftDates.map((shiftDate) => {
            return {
                start_date: formatForServer(shiftDate.startDate),
                end_date: formatForServer(shiftDate.endDate),
            }
        })
    } else if (form.startDate && form.endDate) {
        data.start_date = formatForServer(form.startDate)
        data.end_date = formatForServer(form.endDate)
    }

    if (form.published) {
        data.published = true
    }

    if (form.assigneeId) {
        data.assignee_id = form.assigneeId
    }

    if (form.open) {
        data.open = true
    }

    if (form.approvalRequired) {
        data.approval_required = true
    }

    if (form.rateAmount) {
        data.rate_amount = form.rateAmount
    }

    if (form.note) {
        data.note = form.note
    }

    if (form.skillIds) {
        data.skill_ids = form.skillIds
    }

    if (form.employeeIds) {
        data.employee_ids = form.employeeIds
    }

    return data
}

export const create = async (siteId: SiteID, form: ShiftCreateForm): Promise<{ shift: Shift }> => {
    const data = createFormToData(form)

    const response = await axiosInstance.post(`api/orgs/${siteId}/shifts`, data)

    const result = response.data.data as ShiftDto

    return {
        shift: shiftFromDto(result),
    }
}

export const createBulk = async (siteId: SiteID, form: ShiftCreateForm): Promise<{ shifts: Shift[] }> => {
    const data = createFormToData(form)

    const response = await axiosInstance.post(`api/orgs/${siteId}/shifts/bulk`, data)

    const shiftJson = response.data.data as ShiftDto[]

    return {
        shifts: shiftJson.map((shift) => { return shiftFromDto(shift) }),
    }
}

export const fetchActivity = async (siteId: SiteID, shiftId: ShiftID): Promise<{ activities: ShiftActivity[], pagination: Pagination }> => {
    const params = {
        'includes': ['causer'],
    }

    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/${shiftId}/activity`, { params })

    const data = response.data.data as ShiftActivityDto[]

    return {
        activities: data.map((activity) => {
            return shiftActivityFromDto(activity)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const remind = async (siteId: SiteID, shiftId: ShiftID, form: { method: 'push' | 'email' | 'sms' }): Promise<void> => {
    await axiosInstance.post(`api/orgs/${siteId}/shifts/${shiftId}/remind`, {
        method: form.method,
    })
}

export const downloadActivityExport = async (siteId: SiteID): Promise<void> => {
    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/export`, { responseType: 'blob' })

    const blobUrl = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = blobUrl
    link.setAttribute('download', 'shifts.xlsx')
    document.body.appendChild(link)
    link.click()

    // Clean up
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(link)
}